import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Users, MessageSquareQuote, MapPin, Hash, Locate, Check } from "lucide-react";

const AUDIENCES = [
    { key: "public", label: "Anyone" },
    { key: "friends", label: "Close friends" },
    { key: "partner", label: "Someone special" },
    { key: "self", label: "Just me" },
];
const TONES = [
    { key: "native", label: "Native — raw, lowercase, slangy" },
    { key: "clean", label: "Clean — polished, complete sentences" },
];
const TAG_OPTIONS = [
    "growth", "heartbreak", "joy", "anxiety", "tired",
    "hopeful", "lonely", "grateful", "angry", "soft",
    "proud", "stuck", "free",
];

export default function PersonalizePanel({ value, onChange, stepNumber }) {
    const [open, setOpen] = useState(false);
    // Track whether the user has touched the panel THIS session — green state
    // only fires after a fresh interaction so it doesn't read as 'done' on
    // first load just because we hydrated saved preferences.
    const [touched, setTouched] = useState(false);

    const set = (patch) => { setTouched(true); onChange({ ...value, ...patch }); };
    const toggleTag = (t) => {
        setTouched(true);
        const tags = value.tags || [];
        const next = tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t].slice(0, 5);
        onChange({ ...value, tags: next });
    };

    const detectLocation = async () => {
        if (!navigator.geolocation) return;
        setTouched(true);
        try {
            const pos = await new Promise((res, rej) =>
                navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000 })
            );
            const { latitude: lat, longitude: lon } = pos.coords;
            const r = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`,
                { headers: { "Accept-Language": "en" } }
            );
            const j = await r.json();
            const city = j.address?.city || j.address?.town || j.address?.village || j.address?.suburb || j.address?.state;
            if (city) set({ locality: city });
        } catch (e) {
            console.warn("geolocation failed", e);
        }
    };

    const summary = [];
    if (value.audience) summary.push(AUDIENCES.find((a) => a.key === value.audience)?.label);
    if (value.tone) summary.push(value.tone === "clean" ? "clean" : "native");
    if (value.locality) summary.push(value.locality);
    if (value.tags?.length) summary.push(`${value.tags.length} tag${value.tags.length > 1 ? "s" : ""}`);

    const isDone = touched && summary.length > 0;

    return (
        <div
            className={`w-full rounded-2xl border backdrop-blur-md overflow-hidden transition-colors ${
                isDone
                    ? "border-emerald-400/55 bg-emerald-400/10"
                    : "border-app/30 bg-surface-1"
            }`}
            data-testid="personalize-panel"
        >
            <button
                onClick={() => setOpen((o) => !o)}
                data-testid="personalize-toggle"
                className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                    isDone ? "hover:bg-emerald-400/14" : "hover:bg-surface-2"
                }`}
            >
                <div className="flex items-center gap-3">
                    {stepNumber != null && (
                        <motion.span
                            key={isDone ? "done" : "step"}
                            initial={{ scale: 0.6, rotate: -45 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 380, damping: 18 }}
                            className={`flex items-center justify-center h-7 w-7 rounded-full shrink-0 ${
                                isDone
                                    ? "bg-emerald-400 text-emerald-950 shadow-[0_0_14px_rgba(52,211,153,0.55)]"
                                    : "bg-surface-2 text-app-2 border border-app/30"
                            }`}
                        >
                            {isDone ? (
                                <Check className="w-4 h-4" strokeWidth={3.2} />
                            ) : (
                                <span className="font-sans text-[11px] font-semibold">{stepNumber}</span>
                            )}
                        </motion.span>
                    )}
                    <div className="flex flex-col">
                        <span className={`font-sans text-[10px] uppercase tracking-[0.4em] ${isDone ? "text-emerald-200" : "text-app-3"}`}>
                            {isDone ? "personalized" : "personalize"}
                        </span>
                        <span className="font-serif italic text-app-2 text-sm mt-0.5">
                            {summary.length > 0 ? summary.join(" · ") : "tell us a little more — optional"}
                        </span>
                    </div>
                </div>
                <ChevronDown
                    className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""} ${isDone ? "text-emerald-200" : "text-app-3"}`}
                    strokeWidth={1.5}
                />
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-5 space-y-5 border-t border-app/20">
                            {/* Audience */}
                            <div>
                                <div className="flex items-center gap-2 mb-2 mt-4">
                                    <Users className="w-3.5 h-3.5 text-app-3" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                        who's this for
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {AUDIENCES.map((a) => (
                                        <button
                                            key={a.key}
                                            onClick={() => set({ audience: value.audience === a.key ? null : a.key })}
                                            data-testid={`audience-${a.key}`}
                                            className={`px-3 py-1.5 rounded-full border text-xs font-sans transition-colors ${
                                                value.audience === a.key
                                                    ? "border-app bg-cta text-app-inverse"
                                                    : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                            }`}
                                        >
                                            {a.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Tone */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <MessageSquareQuote className="w-3.5 h-3.5 text-app-3" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                        lingo
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {TONES.map((t) => (
                                        <button
                                            key={t.key}
                                            onClick={() => set({ tone: value.tone === t.key ? null : t.key })}
                                            data-testid={`tone-${t.key}`}
                                            className={`px-3 py-1.5 rounded-full border text-xs font-sans transition-colors ${
                                                value.tone === t.key
                                                    ? "border-app bg-cta text-app-inverse"
                                                    : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Locality */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <MapPin className="w-3.5 h-3.5 text-app-3" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                        from where
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        value={value.locality || ""}
                                        onChange={(e) => set({ locality: e.target.value })}
                                        placeholder="Brooklyn, Lagos, Tokyo…"
                                        data-testid="locality-input"
                                        className="flex-1 bg-surface-2 border border-app/30 rounded-full px-4 py-2 text-app font-sans text-sm outline-none focus:border-app/60 placeholder:text-app-3"
                                    />
                                    <button
                                        onClick={detectLocation}
                                        data-testid="locality-detect"
                                        className="px-3 py-2 rounded-full border border-app/30 text-app-2 hover:text-app hover:border-app/60 transition-colors"
                                        aria-label="Detect location"
                                    >
                                        <Locate className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>

                            {/* Tags */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Hash className="w-3.5 h-3.5 text-app-3" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                        what applies — pick up to 5
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {TAG_OPTIONS.map((t) => (
                                        <button
                                            key={t}
                                            onClick={() => toggleTag(t)}
                                            data-testid={`tag-${t}`}
                                            className={`px-3 py-1.5 rounded-full border text-xs font-sans transition-colors ${
                                                value.tags?.includes(t)
                                                    ? "border-app bg-cta text-app-inverse"
                                                    : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                            }`}
                                        >
                                            #{t}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
